import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './LiveApps.css';
import './Testimonials.css';
import { TESTIMONIAL_BY_APP } from './Testimonials';
import PageHero from './PageHero';
import { IconExternal } from './Icons';

const LIVE_APPS = [
  {
    id: 'coryblossom',
    name: 'Cory Blossom',
    url: 'https://coryblossoms.netlify.app/',
    displayUrl: 'coryblossoms.netlify.app',
    tagline: 'Finger Lakes florist & flower subscriptions',
    summary:
      'A boutique floral brand in the Finger Lakes region. Full e-commerce experience: a storefront with seasonal bouquet catalog, subscription billing for spring/summer flower drops, and a regional delivery map — all wrapped in editorial brand photography.',
    highlights: [
      'E-commerce storefront with seasonal bouquet catalog',
      'Subscription flow for monthly Wrapped Bouquet drops',
      'Finger Lakes delivery map and service-area logic',
      'Brand-forward photography and editorial layout',
    ],
    quoteTopic: 'Custom e-commerce or floral brand site like Cory Blossom',
  },
  {
    id: 'adams',
    name: 'Kevin Adams, Mayor',
    url: 'https://kevindadams.com/',
    displayUrl: 'kevindadams.com',
    tagline: 'Village of Horseheads — Office of the Mayor',
    summary:
      'The official site for Kevin Adams, sitting Mayor of the Village of Horseheads, NY. Polished bio, qualifications, vision, and a contact funnel — built so the office can post updates fast and engage residents and supporters ahead of the re-election cycle.',
    highlights: [
      'Bio, qualifications, vision, and contact pages',
      'Animated, mobile-first layout for door-to-door sharing',
      'EmailJS-wired contact funnel for supporter and resident outreach',
      'Editable content structure for fast updates',
    ],
    quoteTopic: 'Campaign or elected-official website',
  },
  {
    id: 'majorcontent',
    name: 'Major Content',
    url: 'https://www.majorcontent.com/',
    displayUrl: 'majorcontent.com',
    tagline: 'TV, film & literary management',
    summary:
      "The company site for Major Content, Raymond Garcia's TV, film, and literary management firm. It presents the company's scripted and unscripted production work alongside the authors it represents, with a portfolio of titles adapted from bestselling books.",
    highlights: [
      'Company, TV | Film, Books, and Founder sections',
      'Portfolio grid of titles with credits and detail views',
      'Editorial layout suited to an entertainment brand',
      'Custom domain with Terms and Privacy pages',
    ],
    quoteTopic: 'Media or entertainment company website like Major Content',
  },
  {
    id: 'investnyou',
    name: 'InvestNYou',
    url: 'https://investnyou.org/',
    displayUrl: 'investnyou.org',
    tagline: 'Information & investment tooling',
    summary:
      'A custom information management platform with structured data, polished workflows, and a UI built for users to explore and act on complex information with confidence.',
    highlights: [
      'Custom information architecture and user flows',
      'Secure, maintainable web application delivery',
      'Designed for ongoing updates and real-world use',
    ],
    quoteTopic: 'Information system similar to InvestNYou',
  },
  {
    id: 'luriecup',
    name: 'Lurie Cup',
    url: 'https://luriecup.org/',
    displayUrl: 'luriecup.org',
    tagline: 'Charity soccer tournament',
    summary:
      'Web presence for the Lurie Cup charity soccer match, whose proceeds support hospital care. Built so organizers can share the story, drive registrations and donations, and keep supporters informed in one place.',
    highlights: [
      'Clear messaging for a nonprofit fundraising event',
      'Trustworthy, easy-to-scan layout for donors and participants',
      'Mobile-friendly experience for fans on the go',
    ],
    quoteTopic: 'Nonprofit / event website like Lurie Cup',
  },
  {
    id: 'proscape',
    name: 'ProScape',
    url: 'https://proscapeny.netlify.app/',
    displayUrl: 'proscapeny.netlify.app',
    tagline: 'Landscaping business site',
    summary:
      'A full small-business web presence: clear home story, dedicated services pages, and contact flows — including quote capture so homeowners can request estimates without friction.',
    highlights: [
      'Home, services, and contact experience tuned for local SEO and trust',
      'Quote / request flow aligned with how landscaping jobs are booked',
      'Responsive layout for phone-first customers on the job',
    ],
    quoteTopic: 'Small business site with services, contact, and quote flow',
  },
  {
    id: 'binshine',
    name: 'BinShine',
    url: 'https://bin-shine.netlify.app/',
    displayUrl: 'bin-shine.netlify.app',
    tagline: 'Curbside bin cleaning — Connecticut',
    heavy: true,
    summary:
      'A booking platform for a Connecticut curbside bin-cleaning service. An interactive 3D tour walks visitors through the wash, then the booking form creates a customer account so plans, extra cleans, invoices, and reward points can all be managed online.',
    highlights: [
      'Interactive 3D tour of the cleaning process, rendered with WebGL',
      'Monthly, quarterly, and one-time plans with seasonal billing',
      'Booking flow that sets up a customer account and auto-rebooks cleans',
      'Customer portal plus a staff office view, with before/after gallery',
    ],
    quoteTopic: 'Service business site with booking and customer accounts like BinShine',
  },
  {
    id: 'docreplay',
    name: 'DocReplay',
    url: 'https://docreplay.netlify.app/',
    displayUrl: 'docreplay.netlify.app',
    tagline: "Replay your Google Doc's history",
    summary:
      'A productivity tool that turns Google Docs revision history into a playable timeline — so writers, teachers, and teams can watch a document evolve and understand how it was actually written.',
    highlights: [
      'Reconstructs document history into a clean, scrubbable timeline',
      'Designed for educators checking authorship and writing process',
      'Polished SaaS-style UI with pricing, sign-in, and sign-up flows',
    ],
    quoteTopic: 'SaaS / productivity web app like DocReplay',
  },
  {
    id: 'davisreel',
    name: 'Davis Reel',
    url: 'https://davisreel.com/',
    displayUrl: 'davisreel.com',
    tagline: 'Interactive 3D movie review platform',
    heavy: true,
    summary:
      "A movie review platform built as a real-time 3D experience. Visitors move through a rendered space to browse Davis's reviews instead of scrolling a conventional feed of posts — the presentation is part of the review, not a wrapper around it.",
    highlights: [
      'Real-time 3D environment rendered in-browser with WebGL',
      "Interactive navigation through Davis's movie reviews",
      'Review content staged in-scene rather than in a standard blog layout',
      'Custom domain on a purpose-built content platform',
    ],
    quoteTopic: 'Interactive 3D content platform like Davis Reel',
  },
  {
    id: 'neunseinseins',
    name: 'Porsche 911 GT3 RS',
    url: 'https://neuneinseins.netlify.app/',
    displayUrl: 'neunseinseins.netlify.app',
    tagline: 'Interactive 3D car experience',
    heavy: true,
    summary:
      'A real-time 3D showcase of the Porsche 911 GT3 RS, rendered live in the browser with WebGL. Spin the car, explore the details, and experience cinematic lighting — our take on a flagship automotive web experience.',
    highlights: [
      'Real-time 3D car model rendered in-browser with WebGL',
      'Interactive camera controls and detail exploration',
      'Cinematic lighting and high-fidelity materials',
    ],
    quoteTopic: 'Interactive 3D / WebGL product experience',
  },
  {
    id: 'kalonlong',
    name: 'Kalon Long',
    url: 'https://kalonlong.netlify.app/',
    displayUrl: 'kalonlong.netlify.app',
    tagline: '3D portfolio for artist @kalon.long',
    heavy: true,
    summary:
      'An immersive 3D portfolio for visual artist Kalon Long (@kalon.long on Instagram). Visitors move through an interactive 3D space to explore the artwork — a gallery experience that lives in the browser instead of a flat grid of images.',
    highlights: [
      "Immersive 3D gallery built around an artist's portfolio",
      'Interactive WebGL navigation through the artwork',
      'Exhibition-style, brand-forward presentation',
    ],
    quoteTopic: '3D / WebGL portfolio site like Kalon Long',
  },
];

const LiveApps = () => {
  // Heavy WebGL apps load their iframe only after the visitor opts in.
  const [loaded, setLoaded] = useState({});

  return (
    <>
      <PageHero
        eyebrow="Work / Live apps"
        title="Shipped, deployed, and serving real users."
        lede="Not mockups. Not demos. Every project below is in production right now — open any of them in your browser."
        meta={[`${LIVE_APPS.length} live products`, 'E-commerce · Gov · Media · Nonprofit · SaaS · 3D']}
      >
        <Link to="/contact?reason=quote" className="btn btn--primary">
          Start a project <span className="btn-arrow">→</span>
        </Link>
        <Link to="/projects" className="btn btn--ghost">
          Browse all projects
        </Link>
      </PageHero>

      <main className="main-content" id="main">
        <section className="section live-apps-section">
          <div className="container">
            <ul className="live-apps-list" data-stagger="0.05">
              {LIVE_APPS.map((app, idx) => (
                <li className="live-app-card reveal reveal-up" key={app.id}>
                  <span className="live-app-index" aria-hidden="true">
                    {String(idx + 1).padStart(2, '0')}
                  </span>

                  <div
                    className={`live-app-preview${loaded[app.id] ? ' is-loaded' : ''}`}
                  >
                    {/* Nothing is embedded until asked for — eleven live sites
                        running at once is a lot of tabs' worth of work. */}
                    {loaded[app.id] ? (
                      <iframe src={app.url} title={`${app.name} live preview`} loading="lazy" />
                    ) : (
                      <button
                        type="button"
                        className="live-app-poster"
                        onClick={() => setLoaded((prev) => ({ ...prev, [app.id]: true }))}
                        aria-label={`Load the live preview of ${app.name}`}
                      >
                        <span className="live-app-poster__mark" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none">
                            <path
                              d="M8.5 6.8l9 5.2-9 5.2z"
                              stroke="currentColor"
                              strokeWidth="1.4"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span className="live-app-poster__title">
                          {app.heavy ? 'Load 3D preview' : 'Load live preview'}
                        </span>
                        <span className="live-app-poster__note">
                          {app.heavy
                            ? 'Heavy WebGL build — loads on demand, then you can spin it right here.'
                            : 'Loads the real site in this frame. Scroll and click inside it once it appears.'}
                        </span>
                        <span className="live-app-poster__url">{app.displayUrl}</span>
                      </button>
                    )}

                    <a
                      className="live-app-preview__open"
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open ${app.name} in a new tab`}
                    >
                      Open live site
                      <IconExternal />
                    </a>
                  </div>

                  <div className="live-app-body">
                    <div className="live-app-meta">
                      <span className="tag tag--brand">
                        <span className="dot-live" aria-hidden="true" />
                        Live
                      </span>
                      <span className="live-app-tagline">{app.tagline}</span>
                    </div>

                    <h2 className="live-app-name">{app.name}</h2>

                    <a
                      className="live-app-url"
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {app.displayUrl}
                    </a>

                    <p className="live-app-summary">{app.summary}</p>

                    <ul className="live-app-highlights">
                      {app.highlights.map((h) => (
                        <li key={h}>{h}</li>
                      ))}
                    </ul>

                    {TESTIMONIAL_BY_APP[app.id] && (
                      <figure className="live-app-testimonial">
                        <blockquote>&ldquo;{TESTIMONIAL_BY_APP[app.id].quote}&rdquo;</blockquote>
                        <figcaption>
                          {TESTIMONIAL_BY_APP[app.id].author}
                          <span>{TESTIMONIAL_BY_APP[app.id].role}</span>
                        </figcaption>
                      </figure>
                    )}

                    <div className="live-app-ctas">
                      <a
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn--ghost btn--sm"
                      >
                        Visit {app.displayUrl}
                        <IconExternal />
                      </a>
                      <Link
                        to={`/contact?reason=quote&topic=${encodeURIComponent(app.quoteTopic)}`}
                        className="btn btn--link btn--sm"
                      >
                        Build something similar <span className="btn-arrow">→</span>
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <p className="live-apps-footnote reveal reveal-fade">
              Previews load only when you ask for one, so this page stays light.
              Some browsers block in-frame embedding entirely &mdash; if a frame stays
              blank after loading, use &ldquo;Open live site&rdquo; to view it directly.
            </p>
          </div>
        </section>

        <section className="section section--alt live-apps-cta">
          <div className="container container--narrow live-apps-cta-inner">
            <p className="eyebrow eyebrow--plain">Your turn</p>
            <h2 className="section-title reveal reveal-up">Want a live app of your own?</h2>
            <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
              Marketing site, information system, or custom web app — we ship products
              that go to production, not decks that sit in a drive.
            </p>
            <div className="btn-row reveal reveal-up" style={{ '--reveal-delay': '0.16s' }}>
              <Link to="/contact?reason=quote" className="btn btn--primary btn--lg">
                Start a project <span className="btn-arrow">→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default LiveApps;
